// The workweek (brief 1D-5; PLAN.md 7.3; DECISIONS.md 10/24 week start). Pure: no database, no
// clock.
//
// - Each company picks the weekday its week starts on (Sunday by default). A week is a fixed seven
//   days; nothing anywhere assumes Sunday to Saturday.
// - A change takes effect from the next full week: the first day with the new start after the
//   week that's running ends. The days between the last week on the old start and the first day
//   on the new one (one to six) form one short changeover week, so this week keeps its start, the
//   next full week starts on the new day, no week overlaps another, and no week is ever longer
//   than seven days, so none hides overtime (brief 1D-6; 1E's overtime reads the short week).
// - Every change is kept with its date (who, when, from, to, the day it starts), so a past week
//   keeps the start it had. A change made while another is waiting replaces the waiting one.
// - A client type can bill on its own week (stored now; 1E reads it).

export const WEEK_DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;
export type WeekDayName = (typeof WEEK_DAYS)[number];

export const WEEK_DAY_LABEL: Record<WeekDayName, string> = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
};

/** One change of the week start, as kept in week_start_history. */
export interface WeekChange {
  /** The user who made it, and their name then. */
  by: string;
  byName: string;
  /** When it was made (ISO). */
  at: string;
  from: WeekDayName;
  to: WeekDayName;
  /** The first day of the first week on the new start (YYYY-MM-DD). */
  startsOn: string;
}

/** The company's week, from tenant_settings: week_start, week_start_from (a day, or null or ""
 * for always), week_start_history. */
export interface WeekSetting {
  start: WeekDayName;
  from: string | null;
  history: WeekChange[];
}

/** A company that never chose: Sunday, always. */
export const SUNDAY_WEEK: WeekSetting = { start: "sunday", from: null, history: [] };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** 0 Sunday .. 6 Saturday, for a YYYY-MM-DD day. */
// ---- The company's time zone (brief 1E-8) --------------------------------------------------------

/** A company's time zone until it sets another (CLAUDE.md: America/New_York by default). */
export const DEFAULT_TIME_ZONE = "America/New_York";

/** The time zone setting as written, when it's one the clock knows; else the default. */
export function readTimeZone(v: unknown): string {
  if (typeof v !== "string" || !v.trim()) return DEFAULT_TIME_ZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: v.trim() });
    return v.trim();
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

/** The US time zones a company picks from, by name (brief 1E-9). */
export const US_TIME_ZONES: readonly { value: string; label: string }[] = [
  { value: DEFAULT_TIME_ZONE, label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

/**
 * A new time zone for the company: one of the listed US zones. It applies to records made from
 * now on; a time already saved keeps the moment it was saved.
 */
export function readTimeZoneChoice(
  v: unknown,
): { ok: true; value: string } | { ok: false; error: string } {
  const zone = typeof v === "string" ? v.trim() : "";
  if (US_TIME_ZONES.some((z) => z.value === zone)) return { ok: true, value: zone };
  return { ok: false, error: "Pick one of the listed time zones." };
}

/** A time zone in words: "Eastern time" for America/New_York; else its city ("Guam time"). */
export const zoneWords = (zone: string): string =>
  `${US_TIME_ZONES.find((z) => z.value === zone)?.label ?? (zone.split("/").pop() ?? zone).replace(/_/g, " ")} time`;

/** The day it is at a moment in a time zone, as YYYY-MM-DD. */
export function zonedDay(at: Date, zone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/** A day and wall-clock time in a time zone ("2026-10-27", "14:30") as the moment it is. */
export function zonedInstant(day: string, time: string, zone: string): Date {
  const guess = new Date(`${day}T${time}:00Z`);
  const shown = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(guess);
  const p = (t: string) => Number(shown.find((x) => x.type === t)?.value ?? 0);
  const asUtc = Date.UTC(p("year"), p("month") - 1, p("day"), p("hour"), p("minute"));
  return new Date(guess.getTime() + (guess.getTime() - asUtc));
}

/** MM/DD/YYYY, as the screens show a date. */
export const usDay = (d: string) => `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}`;

export const dowOf = (day: string) => new Date(`${day}T12:00:00Z`).getUTCDay();

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const isWeekDay = (v: unknown): v is WeekDayName =>
  typeof v === "string" && (WEEK_DAYS as readonly string[]).includes(v);

function isChange(v: unknown): v is WeekChange {
  if (typeof v !== "object" || v === null) return false;
  const c = v as Record<string, unknown>;
  return (
    isWeekDay(c.from) &&
    isWeekDay(c.to) &&
    typeof c.startsOn === "string" &&
    DAY.test(c.startsOn) &&
    typeof c.at === "string" &&
    typeof c.by === "string"
  );
}

/** Reads the three settings as saved, Sunday for anything missing or broken. */
export function readWeekSetting(rows: {
  week_start?: unknown;
  week_start_from?: unknown;
  week_start_history?: unknown;
}): WeekSetting {
  const start = isWeekDay(rows.week_start) ? rows.week_start : "sunday";
  const from =
    typeof rows.week_start_from === "string" && DAY.test(rows.week_start_from)
      ? rows.week_start_from
      : null;
  const history = Array.isArray(rows.week_start_history)
    ? rows.week_start_history.filter(isChange).map((c) => ({ ...c, byName: c.byName ?? "" }))
    : [];
  return { start, from, history };
}

interface Period {
  start: WeekDayName;
  /** The first day it applies to; null: from the beginning. */
  from: string | null;
}

/** The starts in force over time, oldest first. A later change replaces one still waiting; two
 * periods in a row on the same start are one. */
function periodsOf(s: WeekSetting): Period[] {
  if (!s.history.length) return [{ start: s.start, from: s.from }];
  const sorted = [...s.history].sort((a, b) => a.at.localeCompare(b.at));
  let periods: Period[] = [{ start: sorted[0]?.from ?? s.start, from: null }];
  for (const c of sorted) {
    // A change replaces any still waiting when it was made, and anything from its own start on.
    const made = c.at.slice(0, 10);
    periods = periods.filter((p) => p.from === null || (p.from < c.startsOn && p.from <= made));
    if (periods.at(-1)?.start !== c.to) periods.push({ start: c.to, from: c.startsOn });
  }
  return periods;
}

function weekIn(day: string, periods: readonly Period[]): { start: string; end: string } {
  const mine = [...periods].reverse().find((p) => p.from === null || p.from <= day) ??
    periods[0] ?? { start: "sunday" as const, from: null };
  const first = WEEK_DAYS.indexOf(mine.start);
  const aligned = (d: string) => addDays(d, -((dowOf(d) - first + 7) % 7));
  let start = aligned(day);
  if (mine.from && start < mine.from) start = mine.from;
  const end = addDays(start, 6);
  const next = periods.find((p) => p.from !== null && p.from > day);
  // The week that would run past the new start stops the day before it: the short changeover
  // week, from its old-start day to the day before the new start (one to six days).
  return next?.from && end >= next.from ? { start, end: addDays(next.from, -1) } : { start, end };
}

/**
 * The week a day is in: its first and last day. `weekStart` is a weekday (a fixed start, such as
 * a client type's billing week) or the company's week setting, whose changes over time it follows.
 */
export function weekOf(
  day: string,
  weekStart: WeekDayName | WeekSetting,
): { start: string; end: string } {
  return weekIn(
    day,
    typeof weekStart === "string" ? [{ start: weekStart, from: null }] : periodsOf(weekStart),
  );
}

/** The weekday the week a day is in started on, as the company had it then. */
export function weekStartOn(day: string, s: WeekSetting): WeekDayName {
  const periods = periodsOf(s);
  const mine = [...periods].reverse().find((p) => p.from === null || p.from <= day);
  return mine?.start ?? s.start;
}

/** The days of a week, first to last: seven, or one to six in a changeover week. */
export function daysOf(week: { start: string; end: string }): string[] {
  const out: string[] = [];
  for (let d = week.start; d <= week.end; d = addDays(d, 1)) out.push(d);
  return out;
}

/** Weekday numbers (0 Sunday .. 6 Saturday) in the order the week runs from its start. */
export function weekOrder(start: WeekDayName): number[] {
  const i = WEEK_DAYS.indexOf(start);
  return Array.from({ length: 7 }, (_, k) => (i + k) % 7);
}

/**
 * The day a new start takes effect: the first day on the new start after the week running today
 * ends (a change still waiting is left out, since this one replaces it).
 */
export function nextFullWeek(today: string, s: WeekSetting, to: WeekDayName): string {
  const settled = periodsOf(s).filter((p) => p.from === null || p.from <= today);
  let d = addDays(weekIn(today, settled).end, 1);
  while (dowOf(d) !== WEEK_DAYS.indexOf(to)) d = addDays(d, 1);
  return d;
}

/** The start a waiting change moves to, and when; null when nothing waits. */
export function waitingChange(
  today: string,
  s: WeekSetting,
): { to: WeekDayName; startsOn: string } | null {
  const last = periodsOf(s)
    .filter((p) => p.from !== null && p.from > today)
    .at(-1);
  return last?.from ? { to: last.start, startsOn: last.from } : null;
}

/**
 * Changing the start: refused when it's already the start (and nothing waits), else the new
 * setting with the change kept, starting with the next full week. Picking today's start while a
 * change waits undoes the waiting change.
 */
export function changeWeekStart(
  s: WeekSetting,
  to: unknown,
  today: string,
  who: { id: string; name: string },
  at: Date,
): { ok: true; value: WeekSetting; change: WeekChange } | { ok: false; error: string } {
  if (!isWeekDay(to)) return { ok: false, error: "Pick the day the week starts on." };
  const now = weekStartOn(today, s);
  const waiting = waitingChange(today, s);
  if (to === now && !waiting)
    return { ok: false, error: `The week already starts ${WEEK_DAY_LABEL[to]}.` };
  if (waiting && to === waiting.to)
    return {
      ok: false,
      error: `${WEEK_DAY_LABEL[to]} already starts ${usShort(waiting.startsOn)}.`,
    };
  const change: WeekChange = {
    by: who.id,
    byName: who.name,
    at: at.toISOString(),
    from: waiting ? waiting.to : now,
    to,
    startsOn: to === now && waiting ? waiting.startsOn : nextFullWeek(today, s, to),
  };
  const history = [...s.history, change];
  const last = periodsOf({ ...s, history }).at(-1);
  return {
    ok: true,
    value: { start: last?.start ?? to, from: last?.from ?? null, history },
    change,
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Nov 1". */
export function usShort(day: string): string {
  return `${MONTHS[+day.slice(5, 7) - 1]} ${+day.slice(8, 10)}`;
}

/**
 * The Week card's line: "Starts Sunday" or, with a change waiting, "Starts Sunday until Nov 1,
 * then Monday" (the last day on the old start, then the new one).
 */
export function weekStartWords(today: string, s: WeekSetting): string {
  const now = weekStartOn(today, s);
  const waiting = waitingChange(today, s);
  if (!waiting) return `Starts ${WEEK_DAY_LABEL[now]}`;
  return `Starts ${WEEK_DAY_LABEL[now]} until ${usShort(addDays(waiting.startsOn, -1))}, then ${WEEK_DAY_LABEL[waiting.to]}`;
}

/**
 * The Week card's note about the changeover week, while a change waits: "Nov 1 is a 1-day week,
 * then weeks start Monday", or "Nov 1 to Nov 6 is a 6-day week, then weeks start Saturday". Null
 * when nothing waits.
 */
export function changeoverWords(today: string, s: WeekSetting): string | null {
  const waiting = waitingChange(today, s);
  if (!waiting) return null;
  const short = weekOf(addDays(waiting.startsOn, -1), s);
  const days = daysOf(short).length;
  if (days >= 7) return null;
  const when =
    days === 1 ? usShort(short.start) : `${usShort(short.start)} to ${usShort(short.end)}`;
  return `${when} is a ${days}-day week, then weeks start ${WEEK_DAY_LABEL[waiting.to]}`;
}

/** A client type's billing week: null follows the company's workweek. */
export function billingWeekOf(
  day: string,
  billingWeek: WeekDayName | null,
  company: WeekSetting,
): { start: string; end: string } {
  return weekOf(day, billingWeek ?? company);
}

/** Reads a billing week choice: "" or null for the workweek, else a weekday. */
export function readBillingWeek(
  v: unknown,
): { ok: true; value: WeekDayName | null } | { ok: false; error: string } {
  if (v === null || v === "" || v === undefined) return { ok: true, value: null };
  if (isWeekDay(v)) return { ok: true, value: v };
  return {
    ok: false,
    error: "A billing week starts on a weekday or follows the company workweek.",
  };
}

/** A month calendar's head for the company's week: the weekday names from its start, and how many
 * blank cells come before the 1st. */
export function monthHead(
  first: string,
  start: WeekDayName,
  names: readonly string[],
): { weekdays: string[]; lead: number } {
  const order = weekOrder(start);
  return {
    weekdays: order.map((i) => names[i] ?? ""),
    lead: order.indexOf(dowOf(first)),
  };
}
